// Motion helpers for the homepage. Everything here holds still for people who ask
// for reduced motion and for the prerenderer (headless Chrome snapshots the page
// without scrolling, so anything waiting for a scroll must already be in place).
import { useEffect, useRef, useState, type ReactNode } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

export const isPrerender = () => typeof navigator !== "undefined" && /HeadlessChrome/.test(navigator.userAgent);
export const EASE = [0.22, 1, 0.36, 1] as const;

export const useStill = () => {
  const reduce = useReducedMotion();
  return !!reduce || isPrerender();
};

/** Content that rises into place as it scrolls into view (Attio's feel): a longer
 *  lift than Reveal, with a short blur that clears as it lands. */
export const Rise = ({
  children,
  delay = 0,
  y = 28,
  className,
  as = "div",
}: {
  children: ReactNode;
  delay?: number;
  y?: number;
  className?: string;
  as?: "div" | "li";
}) => {
  const still = useStill();
  if (still) {
    const Tag = as;
    return <Tag className={className}>{children}</Tag>;
  }
  const M = as === "li" ? motion.li : motion.div;
  return (
    <M
      className={className}
      initial={{ opacity: 0, y, filter: "blur(6px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay, ease: EASE }}
    >
      {children}
    </M>
  );
};

/**
 * Steps 0..count-1 while the element is on screen, then holds on the last step for
 * `hold` ms and starts again. Returns the ref to attach and the current step.
 * Still pages show the last step: the finished result tells the story best.
 */
export function useStory<T extends HTMLElement = HTMLDivElement>(count: number, ms: number, hold = 1800, key: unknown = null) {
  const ref = useRef<T>(null);
  const inView = useInView(ref, { margin: "-60px" });
  const still = useStill();
  const [step, setStep] = useState(still ? count - 1 : 0);

  useEffect(() => {
    setStep(still ? count - 1 : 0);
  }, [key, still, count]);

  useEffect(() => {
    if (still || !inView) return;
    const last = step >= count - 1;
    const t = setTimeout(() => setStep((s) => (s >= count - 1 ? 0 : s + 1)), last ? hold : ms);
    return () => clearTimeout(t);
  }, [still, inView, step, count, ms, hold]);

  return { ref, step, inView, still };
}

/** Types `text` out once `start` is true; shows it whole when still. */
export function useTyped(text: string, start: boolean, speed = 28) {
  const still = useStill();
  const [n, setN] = useState(still ? text.length : 0);
  useEffect(() => {
    if (still) return setN(text.length);
    if (!start) return setN(0);
    if (n >= text.length) return;
    const t = setTimeout(() => setN((v) => v + 1), speed);
    return () => clearTimeout(t);
  }, [start, n, text, speed, still]);
  useEffect(() => {
    if (!still) setN(0);
  }, [text, still]);
  return text.slice(0, n);
}

/** Small grey window dots: the product windows stay in the site's colours. */
export const WindowDots = ({ dark = false }: { dark?: boolean }) => (
  <span className="flex items-center gap-1.5">
    {[0, 1, 2].map((i) => (
      <span key={i} className={`h-2.5 w-2.5 rounded-full ${dark ? "bg-[hsl(0_0%_30%)]" : "bg-[hsl(0_0%_84%)]"}`} />
    ))}
  </span>
);

/** A product's own mark with its name: tells a reader which product a picture shows. */
export const ProductChip = ({ logo, name, dark = false }: { logo: string; name: string; dark?: boolean }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11.5px] font-semibold ${
      dark ? "border-[hsl(0_0%_100%/0.14)] bg-[hsl(0_0%_100%/0.06)] text-[hsl(0_0%_92%)]" : "border-border bg-background text-foreground"
    }`}
  >
    <img src={logo} alt="" className={`h-3.5 w-3.5 brightness-0 ${dark ? "invert" : ""}`} />
    {name}
  </span>
);
